import { ReactNode } from 'react';
import { Box, Stack } from '@mui/material';
import { Link } from 'react-router';
import { SentryErrorBoundary } from '@retail/components';
import { Paper } from '@shared/components';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import { BlockHeading } from './BlockHeading';
import { BlockBody } from './BlockBody';
import { BlockErrorState } from './BlockErrorState';

export interface DashboardBlockProps {
  title?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  to?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isEmpty?: boolean;
  emptyMessage?: ReactNode;
  minHeight?: number | string;
  children?: ReactNode;
  paperProps?: React.ComponentProps<typeof Paper>;
}

export const DashboardBlock = ({
  title,
  icon,
  action,
  to,
  isLoading,
  isError,
  onRetry,
  isEmpty,
  emptyMessage,
  minHeight = 100,
  children,
  paperProps = {},
}: DashboardBlockProps) => {
  const t = useDashboardTFunction();
  const showHeader = Boolean(title || action);
  const { sx, ...otherPaperProps } = paperProps;

  return (
    <Paper
      sx={{
        p: 2.5,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        borderRadius: 2,
        ...sx,
      }}
      {...otherPaperProps}
    >
      {showHeader && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ minHeight: 32 }}
        >
          {to ? (
            <Box
              component={Link}
              to={to}
              sx={{
                minWidth: 0,
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <BlockHeading title={title} icon={icon} to={to} />
            </Box>
          ) : (
            <BlockHeading title={title} icon={icon} to={to} />
          )}
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight }}>
        <SentryErrorBoundary
          fallback={({ resetError }) => (
            <BlockErrorState message={t('dashboard.messages.error')} onRetry={resetError} />
          )}
        >
          <BlockBody
            isLoading={isLoading}
            isError={isError}
            isEmpty={isEmpty}
            emptyMessage={emptyMessage}
            onRetry={onRetry}
          >
            {children}
          </BlockBody>
        </SentryErrorBoundary>
      </Box>
    </Paper>
  );
};
